/**
 * "Where Michael stands with no plan" — the walk-in position, stated under the room price.
 *
 * WHY THIS EXISTS. The Aged Care Analyser used to be silent between entering a room price and
 * choosing a modelling mode; the explanation of the starting position lived only in the closable
 * Insight Panel. A client meeting (2026-08-20) showed what that costs: the reader closed the panel
 * in seconds and then could not work out what the system was doing. This note states the starting
 * position inline — the anchor every strategy is implicitly compared against — and it cannot be
 * closed. Owner-approved direction (v5 mock-up, 2026-08-24).
 *
 * CALCULATION DISCIPLINE: every figure comes from the real engines, and — critically — from the
 * IN-CARE assessment: `calculateFinancials` is called WITH the aged-care residency overrides, the
 * same frame the Cost Overview and Financial Impact panels use. The first cut called it without
 * them and quoted the today-at-home pension beside in-care fees — two frames in one sentence, and
 * the note disagreed with the rail by $7/day (owner caught it, 2026-08-24). The today figure is
 * still fetched, but only to SAY the bridge: "pension $X today → $Y once in care", which is also
 * what explains the before/after pair in the Financial Impact panel. The ONLY derived number is
 * the runway estimate (accessible savings ÷ yearly gap), straight-line arithmetic on engine
 * outputs, worded as "roughly … the projections show it precisely" so it can never be mistaken
 * for the projection engine's answer.
 */
import { withDailyPaymentOverride } from './householdCareCosts.ts';
import { quietCalc } from './quietCalc.ts';
import { careBeatStaggered, gapPhaseTwoLine, phaseFromLabel, roleNames, livingPhaseTwoLine } from './carePhases.ts';
import { villageWalkInLine } from './villageExitWords.ts';
import { shortfallOutlook, careYearsFor, type RunwayFacts } from './shortfallOutlook.ts';
import React, { useEffect, useState } from 'react';
import * as calculationApi from '../../services/calculationApi.ts';
import { agedCareEntrantResidencyOverrides, isIllnessSeparatedCouple, partnerRemainsInFamilyHome } from '../../../../shared/src/calculation/householdStatus.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import { JargonTerm } from '../../components/narrative/index.tsx';
import { PlainWordsCard, PlainWordsBeat } from '../../components/narrative/PlainWords.tsx';
import { MeansTestBeats } from '../../components/insights/MeansTestPosition.tsx';
import { formatCurrency } from '../../utils/index.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

/**
 * The gap-and-runway line, separated from React so its rules are unit-testable:
 * a surplus is said plainly; a shortfall names the yearly figure; a runway is ALWAYS
 * labelled rough and always defers to the projections; no savings → no invented runway.
 */
export function buildWalkInGapLine(input: {
    cashFlowBeforeCare: number;
    yearlyCare: number;
    accessibleSavings: number;
    /**
     * The year the PROJECTION says the savings are exhausted — the same figure the "How long the
     * money lasts" panel shows. Null when it has not been computed yet, or when the savings last
     * beyond the horizon.
     */
    projectedRunwayYears?: number | null;
}): { text: string; runwayYears: number | null; surplus: boolean } {
    const gap = input.cashFlowBeforeCare - input.yearlyCare;
    if (gap >= 0) {
        return { text: `Income would cover everything with ${formatCurrency(gap)} a year to spare.`, runwayYears: null, surplus: true };
    }
    const shortfall = -gap;
    if (input.accessibleSavings <= 0) {
        return {
            text: `Income covers all but ${formatCurrency(shortfall)} a year, and there are no accessible savings to draw on — the projections below show how that gap would be met.`,
            runwayYears: null,
            surplus: false,
        };
    }
    /*
     * THE PROJECTION'S ANSWER, NEVER OUR OWN DIVISION (owner-caught on production, 2026-09-10).
     *
     * This used to be `accessible savings ÷ yearly gap`, hedged as "roughly … the projections show
     * it precisely". That hedge was written when the note was the only thing on screen answering
     * the question. It no longer is: the plan summary panel now states the same thing from the real
     * projection, a few centimetres away. On a live client the two read:
     *
     *     this note — "about $700,000 would cover that for roughly 12 years"
     *     the panel  — "Savings last about 10 years"
     *
     * for the SAME untouched walk-in position. Straight-line division cannot see investment growth,
     * the pension moving once in care, care fees changing, or account-based pension minimums — so it
     * was always going to drift from the projection. "Roughly" does not rescue a number that is
     * contradicted on the same screen; it just makes the tool look like it cannot add up, which is
     * the worst of the two readings a client can take.
     *
     * So the note now QUOTES the projection. Where the projection has not answered yet, it says
     * nothing about years rather than inventing a second figure to fill the gap.
     */
    const runwayYears = input.projectedRunwayYears ?? null;
    /*
     * DO NOT BLAME CARE FOR A SHORTFALL THAT WAS ALREADY THERE.
     *
     * Recovered from the retired insight panel, which guarded this with `preCareCashFlow >= 0`. A
     * client whose cashflow was already negative before care begins would otherwise read that the
     * care fees created the gap — which is false, and once a reader catches one false statement they
     * are right to distrust everything under it.
     *
     * `cashFlowBeforeCare` is the same figure the beats above are built from, so the two can never
     * disagree about whether the gap pre-dates the move.
     */
    const alreadyShort = input.cashFlowBeforeCare < 0;
    const opening = alreadyShort
        ? `Income already falls short before any care costs, and the fees add to that — the gap becomes ${formatCurrency(shortfall)} a year`
        : `Income covers all but ${formatCurrency(shortfall)} a year`;
    const savings = `the accessible savings of about ${formatCurrency(input.accessibleSavings)}`;
    return {
        text: runwayYears == null
            // No projected answer yet: name the savings, and let the panel and the projections
            // carry the timing. Better a shorter sentence than a second, different number.
            ? `${opening} — ${savings} are what would cover it. The projections show how long that lasts.`
            : `${opening} — ${savings} would cover that for about ${runwayYears} ${runwayYears === 1 ? 'year' : 'years'}, the same figure shown under "How long the money lasts".`,
        runwayYears,
        surplus: false,
    };
}

/** Accessible savings: what could actually be drawn on to cover a care-fee gap. Display-only. */
export function accessibleSavingsOf(factFind: DraftFactFind): number {
    const assets = factFind.assets || {};
    const sum = (list: { value?: number | string }[] | undefined) =>
        (list || []).reduce((total, item) => total + (parseFloat(String(item?.value)) || 0), 0);
    return sum(assets.bankAccounts) + sum(assets.termDeposits) + sum(assets.shares) + sum(assets.accountBasedPensions);
}

interface WalkInPositionNoteProps {
    factFind: DraftFactFind;
    /** Advertised room price. The note renders nothing without one — we don't guess. */
    radInput: number;
    extraServiceFeeInput?: number;
    /** Adjusted living costs for life in care — see useWalkInBaseline. */
    scenarioLivingExpenses?: number;
    /**
     * The year the PROJECTION says the savings are exhausted, passed in from the analyser so this
     * note and the "How long the money lasts" panel quote ONE number. Null until it is computed —
     * and null means the note stays quiet about years, never that it works one out itself.
     */
    projectedRunwayYears?: number | null;
    /** How long the no-plan savings last and how far the projection reaches (shortfallOutlook.ts). */
    runwayFacts?: RunwayFacts | null;
    /** The second room, where both of a couple enter care — the note then speaks of two rooms. */
    secondRoom?: { person: 'client' | 'partner'; roomPrice: number; careStartDate?: string; extraServiceAnnual?: number; entersLater?: boolean; livingExpensesOnceBoth?: number } | null;
    /** Someone in care now: the position is STAYING PUT in their current room (8 Oct 2026). */
    existingStay?: ExistingStayForWalkIn | null;
    /** Leaving a retirement village (8 Oct 2026): with no plan the exit payment is banked when it arrives. */
    villageExit?: { entitlement: number; refundDate: string } | null;
}

export interface ExistingStayForWalkIn { person: 'client' | 'partner'; facilityName: string; roomPrice: number; refundToday: number; dailyPayment: number }

export interface WalkInCosts { totalDailyCost?: number; dailyAccommodationPayment?: number; }
/** The second stage of a staggered entry: the household once both are in care, and each room's schedule then. */
export interface WalkInPhaseTwo { fromDate: string; financials: WalkInFinancials; firstCosts: WalkInCosts; secondCosts: WalkInCosts | null; }
export interface WalkInFinancials { socialSecurity?: number; cashFlow?: number; }

/**
 * The "do nothing" baseline: the in-care assessment with NO strategy (nothing paid up front,
 * nothing sold). Shared between this note and the strategy reconciliation notes so that every
 * "with no plan" figure on the page is the SAME figure -- one computation, quoted twice, can
 * never disagree with itself the way two hand-rolled ones eventually would.
 */
export function useWalkInBaseline(
    factFind: DraftFactFind,
    radInput: number,
    extraServiceFeeInput = 0,
    /**
     * Living costs the client has adjusted for life in care, from "Confirm your living needs".
     *
     * WHY IT HAS TO REACH HERE (owner-reported, 2026-08-31). The baseline was computed from the
     * fact-find's ORIGINAL living expenses, so changing the adjusted figure moved the plan summary's
     * cashflow and left the walk-in note's "The gap — what it leaves" quoting the old number. Two
     * figures about the same thing, on the same screen, disagreeing.
     *
     * It belongs in the baseline, not only in the strategies: the adjusted figure is what this
     * client will actually spend in care whether or not they adopt a plan, so it is part of doing
     * nothing. Undefined means "not adjusted" and the fact-find's own figure stands.
     */
    scenarioLivingExpenses?: number,
    /** The second room, where both of a couple enter care: priced beside the first, no deposit. */
    secondRoom: { person: 'client' | 'partner'; roomPrice: number; careStartDate?: string; extraServiceAnnual?: number; entersLater?: boolean; livingExpensesOnceBoth?: number } | null = null,
    /** Someone in care now: priced on their current room and deposit, with the daily payment they actually pay. */
    existingStay: ExistingStayForWalkIn | null = null,
) {
    const [costs, setCosts] = useState<WalkInCosts | null>(null);
    const [secondCosts, setSecondCosts] = useState<WalkInCosts | null>(null);
    const [careFinancials, setCareFinancials] = useState<WalkInFinancials | null>(null);
    const [todayFinancials, setTodayFinancials] = useState<WalkInFinancials | null>(null);
    /** The second stage, where the second person enters later (carePhases.ts); null otherwise. */
    const [phase2, setPhase2] = useState<WalkInPhaseTwo | null>(null);

    useEffect(() => {
        if (!(radInput > 0) && !existingStay) { setCosts(null); return; }
        let cancelled = false;
        const modellerResidency = agedCareEntrantResidencyOverrides(factFind as unknown as FactFind);
        // The fact-find every figure below is computed from — the client's own, with living costs
        // replaced by the adjusted figure where they have given one.
        const adjusted = Number(scenarioLivingExpenses);
        const basis: DraftFactFind = Number.isFinite(adjusted) && adjusted > 0
            ? { ...factFind, expenses: { ...(factFind.expenses || {}), livingExpenses: adjusted } } as DraftFactFind
            : factFind;
        const second = secondRoom && secondRoom.roomPrice > 0 ? secondRoom : null;
        const later = !!second && !!second.entersLater;
        // Where the second person enters LATER, the first stage has them still at home: the home
        // exempt from the aged care means test, the couple illness-separated from the first entry.
        const secondKey = second?.person === 'client' ? 'clientResidencyOverride' : 'partnerResidencyOverride';
        const secondRecorded = (second?.person === 'client' ? factFind.client?.currentResidency : factFind.partner?.currentResidency) || 'own_home';
        const firstResidency = later ? { ...modellerResidency, [secondKey]: secondRecorded } : modellerResidency;
        // THREE assessments, used for different jobs. The IN-CARE one (the first stage's residency
        // applied -- the same frame as the Cost Overview and Financial Impact panels) drives every
        // figure: fees, pension, gap. The TODAY one exists only so the pension line can say the
        // bridge. The BOTH-IN-CARE one is the second stage, struck only where there is one.
        // The second stage's living costs, where the plan gives a figure for once both are in care.
        const basisBoth: DraftFactFind = later && second?.livingExpensesOnceBoth != null && second.livingExpensesOnceBoth > 0
            ? { ...basis, expenses: { ...(basis.expenses || {}), livingExpenses: second.livingExpensesOnceBoth } } as DraftFactFind
            : basis;
        Promise.all([
            calculationApi.calculateFinancials(basis, firstResidency as never),
            calculationApi.calculateFinancials(basis),
            later ? calculationApi.calculateFinancials(basisBoth, modellerResidency as never) : Promise.resolve(null),
        ]).then(([inCare, today, both]) => {
            if (cancelled) return;
            setCareFinancials(inCare as WalkInFinancials);
            setTodayFinancials(today as WalkInFinancials);
            const bothFin = (both ?? inCare) as Parameters<typeof calculationApi.calculateAgedCareCosts>[0]['financials'];
            const firstResident = second ? (second.person === 'client' ? 'partner' as const : 'client' as const) : undefined;
            return Promise.all([
                calculationApi.calculateAgedCareCosts({
                    factFind: basis,
                    financials: inCare,
                    // Staying put: the current room and the deposit held, not the new room and nothing paid.
                    radInput: existingStay ? existingStay.roomPrice : radInput,
                    radPaid: existingStay ? existingStay.refundToday : 0,
                    overallStrategy: { ...firstResidency },
                    extraServiceFeeInput: existingStay ? 0 : extraServiceFeeInput,
                    ...(firstResident ? { resident: firstResident } : existingStay ? { resident: existingStay.person } : {}),
                }).then((c) => (existingStay ? withDailyPaymentOverride(c as never, existingStay.dailyPayment) : c)),
                second
                    ? calculationApi.calculateAgedCareCosts({ factFind: basis, financials: bothFin, radInput: second.roomPrice, radPaid: 0, overallStrategy: { ...modellerResidency }, extraServiceFeeInput: second.extraServiceAnnual ?? 0, resident: second.person })
                    : Promise.resolve(null),
                later
                    ? calculationApi.calculateAgedCareCosts({ factFind: basis, financials: bothFin, radInput, radPaid: 0, overallStrategy: { ...modellerResidency }, extraServiceFeeInput, resident: firstResident })
                    : Promise.resolve(null),
            ]).then(([c, c2, c1b]) => {
                if (cancelled) return;
                setCosts(c as WalkInCosts);
                setSecondCosts((c2 as WalkInCosts | null) ?? null);
                setPhase2(later && c1b ? { fromDate: second!.careStartDate || '', financials: bothFin as WalkInFinancials, firstCosts: c1b as WalkInCosts, secondCosts: (c2 as WalkInCosts | null) ?? null } : null);
            });
        }).catch(quietCalc('the walk-in position'));
        return () => { cancelled = true; };
    // The second room is keyed on its fields, not the object: the Analyser builds it afresh each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [factFind, radInput, extraServiceFeeInput, scenarioLivingExpenses, secondRoom?.person, secondRoom?.roomPrice, secondRoom?.careStartDate, secondRoom?.extraServiceAnnual, secondRoom?.entersLater, secondRoom?.livingExpensesOnceBoth, existingStay?.person, existingStay?.roomPrice, existingStay?.refundToday, existingStay?.dailyPayment]);

    return { costs, secondCosts, careFinancials, todayFinancials, phase2 };
}

const RESIDENCY_LABELS: Record<string, string> = {
    renting: 'renting',
    with_family: 'living with family',
    retirement_village: 'living in a retirement village',
};

const WalkInPositionNote = ({ factFind, radInput, extraServiceFeeInput = 0, scenarioLivingExpenses, projectedRunwayYears = null, runwayFacts = null, secondRoom = null, existingStay = null, villageExit = null }: WalkInPositionNoteProps) => {
    const { costs, secondCosts, careFinancials, todayFinancials, phase2 } = useWalkInBaseline(factFind, radInput, extraServiceFeeInput, scenarioLivingExpenses, secondRoom, existingStay);

    if ((!(radInput > 0) && !existingStay) || !costs || !careFinancials || !todayFinancials) return null;
    const financials = careFinancials;

    const goals = (factFind.goals || {}) as { aged_care_for?: string };
    const partnerEntering = goals.aged_care_for === 'partner';
    // Both entering (7 Oct 2026): two rooms, nobody staying in the home, and the couple assessed as
    // living apart. Every sentence below that names who stays at home is skipped.
    const bothEntering = factFind.adviceFor === 'couple' && goals.aged_care_for === 'both' && !!secondRoom && secondRoom.roomPrice > 0;
    const { firstName, secondName } = roleNames(factFind, secondRoom?.person, { first: 'they', second: 'their partner' });
    const enteringName = bothEntering
        ? `${firstName} and ${secondName}`
        : (partnerEntering ? factFind.partner?.firstName : factFind.client?.firstName) || 'they';
    const enteringAre = bothEntering ? 'are' : 'is';
    const stayingName =
        factFind.adviceFor === 'couple' && !bothEntering
            ? (partnerEntering ? factFind.client?.firstName : factFind.partner?.firstName) || 'their partner'
            : null;

    // The first stage's figures. With the second person entering later, their room is not in it:
    // the second stage (phase2) is quoted beside each beat instead.
    const firstDaily = costs.totalDailyCost || 0;
    const secondInFirstStage = bothEntering && !phase2;
    const secondDaily = secondInFirstStage ? (secondCosts?.totalDailyCost || 0) : 0;
    const dailyTotal = firstDaily + secondDaily;
    const dailyDap = (costs.dailyAccommodationPayment || 0) + (secondInFirstStage ? (secondCosts?.dailyAccommodationPayment || 0) : 0);
    const phaseNames = { firstName, secondName, fromLabel: phaseFromLabel(phase2?.fromDate || secondRoom?.careStartDate || '') };
    const phaseTwoDaily = phase2 ? (phase2.firstCosts.totalDailyCost || 0) + (phase2.secondCosts?.totalDailyCost || 0) : 0;
    const phaseTwoDap = phase2 ? (phase2.firstCosts.dailyAccommodationPayment || 0) + (phase2.secondCosts?.dailyAccommodationPayment || 0) : 0;
    const phaseTwoGap = phase2 ? (phase2.financials.cashFlow || 0) - phaseTwoDaily * 365 : 0;
    const careBeat = phase2 ? careBeatStaggered(phaseNames, { firstDaily, phaseTwoFirstDaily: phase2.firstCosts.totalDailyCost || 0, secondDaily: phase2.secondCosts?.totalDailyCost || 0 }) : null;
    const yearlyCare = dailyTotal * 365;
    const pension = financials.socialSecurity || 0;
    const pensionToday = todayFinancials.socialSecurity || 0;
    const pensionRises = Math.round(pension) > Math.round(pensionToday);
    const pensionFalls = Math.round(pension) < Math.round(pensionToday);
    /*
     * THE DOT SAYS WHAT THE MOVEMENT MEANS FOR THE CLIENT (owner, 22 Sep 2026: "if it goes up should
     * we colour code that to green?"). Green when the pension rises, amber when it falls, plain slate
     * when it does not move — the same three-colour code the "What this strategy does" sentence uses,
     * so one rule holds across the page. Never rose: red is for errors. The gap beat follows the same
     * rule — amber for a shortfall, green in the rare case income covers everything. Care and living
     * costs stay slate: a cost is a fact, not good or bad news.
     */
    const pensionTone = pensionRises ? 'good' : pensionFalls ? 'warn' : 'neutral';
    /*
     * Asked in the IN-CARE frame, not today's. This note describes the position once care starts,
     * and the illness-separated assessment is usually the biggest driver of the pension jump it
     * quotes — but with empty options the check asked "are they illness-separated TODAY?" (both
     * still at home: no) and the sentence silently vanished, leaving the jump unexplained
     * (owner-caught, 2026-08-25). Same residency overrides as every figure in this note.
     */
    const illnessSeparated =
        factFind.adviceFor === 'couple' &&
        isIllnessSeparatedCouple(
            factFind as unknown as FactFind,
            agedCareEntrantResidencyOverrides(factFind as unknown as FactFind) as never,
        );
    const homeExemptViaPartner = !bothEntering && partnerRemainsInFamilyHome(factFind as unknown as FactFind, {});

    /*
     * DATA-HYGIENE FLAG (owner-caught, 2026-08-24). A couple's file said the at-home partner was
     * "renting" while the household owned a $2.8m family home; every in-care figure followed the
     * recorded answer and the owner read the result as a calculation bug. The engine now retains
     * homeowner status regardless (SSA s 11A former-home rule, F-AC11), but the aged care means
     * test still rightly depends on whether a protected person LIVES in the home — so an answer
     * that contradicts owning a home is worth a visible question, right where the numbers appear.
     */
    const stayingResidency = partnerEntering ? factFind.client?.currentResidency : factFind.partner?.currentResidency;
    const stayingNotAtHome =
        factFind.adviceFor === 'couple' && !bothEntering &&
        (parseFloat(String(factFind.home?.value)) || 0) > 0 &&
        !!stayingResidency &&
        stayingResidency !== 'own_home' &&
        stayingResidency !== 'aged_care';

    const adjustedLiving = Number(scenarioLivingExpenses) || 0;
    const originalLiving = parseFloat(String(factFind.expenses?.livingExpenses ?? '')) || 0;

    const gapLine = buildWalkInGapLine({
        cashFlowBeforeCare: financials.cashFlow || 0,
        yearlyCare,
        accessibleSavings: accessibleSavingsOf(factFind),
        projectedRunwayYears,
    });
    /*
     * "Don't panic" — but only where it is true (owner, 6 Oct 2026). A shortfall met from savings is
     * normal in care; whether it is fine depends on the savings lasting as long as the care is
     * planned for (shortfallOutlook.ts). Nothing is said when there are no savings to draw on: that
     * case already has its own sentence and is not one to reassure about.
     */
    const outlook = !gapLine.surplus && accessibleSavingsOf(factFind) > 0
        ? shortfallOutlook(runwayFacts ?? (projectedRunwayYears != null ? { runwayYears: projectedRunwayYears, projectedYears: projectedRunwayYears } : null), careYearsFor(factFind), { yearsAlreadySaid: gapLine.runwayYears != null })
        : null;

    return (
        <PlainWordsCard
            eyebrow={existingStay ? `Where ${enteringName} stands by staying at ${existingStay.facilityName || 'the current home'}` : `Where ${enteringName} ${bothEntering ? 'stand' : 'stands'} with no plan`}
            className="mt-5"
            // Read once, then in the way for the rest of the session — it sits between the room
            // price and every control below it (owner, 2026-08-26). Open by default: this is the
            // position every strategy underneath is measured against.
            collapsible
        >
            {/* Same design language as "Understanding your plan" (owner, 2026-08-26): each beat gets
                a dotted blue heading and its own bordered box, instead of three bolded run-in labels
                in a wall of prose. The dot encodes the beat's meaning — slate for a plain statement
                of cost, blue for the entitlement, amber for the gap, which IS a "look at this". */}
            <PlainWordsBeat tone="neutral" heading="Care — what it would cost">
                {/* Says what "no plan" means before the number, so the figure reads as the starting
                    position and not a verdict (owner, 2026-09-15). */}
                <span className="font-semibold text-slate-900">{existingStay ? 'Staying put' : 'With no plan in place'}</span> — {existingStay ? `the current room, the deposit already held, and ${formatCurrency(existingStay.dailyPayment)} a day on the rest of it` : `nothing paid up front, nothing sold or restructured${villageExit ? villageWalkInLine(villageExit) : ''}`} — {careBeat ? (
                    <>
                        {careBeat.untilThen} <span className="font-semibold text-slate-900">{careBeat.fromThen}</span>{' '}
                        <span className="font-semibold">{formatCurrency(phaseTwoDap)}</span> of the two-room figure is the <JargonTerm term="DAP" /> charged on the unpaid rooms
                    </>
                ) : (
                    <>
                        it would cost <span className="font-semibold text-slate-900">{formatCurrency(dailyTotal)} a day</span>
                        {' '}({formatCurrency(yearlyCare)}/yr){secondInFirstStage && <> for the two rooms together — {formatCurrency(firstDaily)} a day for {firstName}&apos;s and {formatCurrency(secondDaily)} a day for {secondName}&apos;s</>} — <span className="font-semibold">{formatCurrency(dailyDap)}</span> of
                        that is the <JargonTerm term="DAP" /> charged on the unpaid {secondInFirstStage ? 'rooms' : 'room'}
                    </>
                )}, which works like rent: none of it comes back.
                {/*
                  * Recovered from the retired insight panel. The daily care fees are charged from the
                  * day care begins, whatever is decided about the room — a client reading a room
                  * price naturally assumes the cost starts with the room, and the basic daily fee,
                  * the means-tested contribution and the hotelling supplement are all owed either
                  * way. Said here because this beat is where the daily cost is first named.
                  */}
                {' '}The rest of these fees start the day care begins, <span className="font-semibold">before any room
                cost is chosen</span>, and are charged whether the room is paid as a lump sum or daily.{' '}
                <span className="font-semibold text-slate-900">Step 3 shows ways to reduce these costs.</span>
            </PlainWordsBeat>
            <PlainWordsBeat
                tone={pensionTone}
                heading={pensionRises ? 'Age Pension — it rises once care starts' : 'Age Pension — what would be paid'}
            >
                {pensionRises ? (
                    <><span className="text-slate-500">{formatCurrency(pensionToday)}/yr today</span> →{' '}
                    <span className="font-semibold text-slate-900">{formatCurrency(pension)}/yr once in care</span></>
                ) : (
                    <span className="font-semibold text-slate-900">{formatCurrency(pension)}/yr</span>
                )}
                {illnessSeparated && (
                    <> — {pensionRises ? 'it rises because they would be assessed as' : 'assessed as'} an{' '}
                    <JargonTerm term="illness-separated couple" />: each is paid at the higher single rate, and the means
                    tests apply more generous thresholds</>
                )}
                {'.'}
                {homeExemptViaPartner && stayingName && (
                    <> Because {stayingName} stays in the home as a <JargonTerm term="protected person" />, the home is
                        exempt from the aged care means test.</>
                )}
                {bothEntering && (
                    <> With nobody left living in the family home, it counts in the aged care means test{secondRoom?.careStartDate ? ` from the day ${secondName} moves in; until then it is exempt because ${secondName} is still living there` : ''} — only up to the capped home value, and it stays exempt for the Age Pension for two years.</>
                )}
            </PlainWordsBeat>
            {/* Which test sets that pension, and what would have to move — read from the IN-CARE
                assessment's own breakdown, so it is the same frame as the figure above it. */}
            <MeansTestBeats
                financials={financials as { socialSecurity?: number; socialSecurityBreakdown?: unknown }}
                isCouple={factFind.adviceFor === 'couple'}
                name={enteringName}
                alsoMoves={<>lowers the means-tested care fees at the same time</>}
                whereNext="The strategies in step 3"
                careDepositRelevant
            />
            {/* Amber earns its place here: a shortfall with a finite runway IS the thing to look at,
                and it is what every strategy below is trying to move. */}
            {/*
              * SAY THAT THE BASELINE MOVED (owner, 2026-08-31). Changing the adjusted living costs
              * quietly re-bases every figure on this screen, including the "no plan" position every
              * strategy is later measured against. A client who typed a number into a box further
              * down has no way of knowing it reached up here unless we tell them.
              */}
            {adjustedLiving > 0 && Math.round(adjustedLiving) !== Math.round(originalLiving) && (
                <PlainWordsBeat tone="neutral" heading="Your living costs — updated">
                    You&apos;ve set living costs to <strong>{formatCurrency(adjustedLiving)}</strong> a year for once{' '}
                    {enteringName} {enteringAre} in care, instead of {formatCurrency(originalLiving)}. Everything on this page now
                    uses that figure — including the &ldquo;no plan&rdquo; position above, which is what every strategy
                    gets compared against.
                    {phase2 && secondRoom?.livingExpensesOnceBoth != null && Math.round(secondRoom.livingExpensesOnceBoth) !== Math.round(adjustedLiving) && (
                        <> {livingPhaseTwoLine(phaseNames, secondRoom.livingExpensesOnceBoth)}</>
                    )}
                </PlainWordsBeat>
            )}
            {/* The second stage's living costs, where they differ and the first figure was not changed. */}
            {phase2 && secondRoom?.livingExpensesOnceBoth != null && Math.round(secondRoom.livingExpensesOnceBoth) !== Math.round(adjustedLiving) && !(adjustedLiving > 0 && Math.round(adjustedLiving) !== Math.round(originalLiving)) && (
                <PlainWordsBeat tone="neutral" heading="Your living costs — from the second stage">
                    {livingPhaseTwoLine(phaseNames, secondRoom.livingExpensesOnceBoth)} Until then they are {formatCurrency(adjustedLiving || originalLiving)} a year.
                </PlainWordsBeat>
            )}
            <PlainWordsBeat tone={gapLine.surplus ? 'good' : outlook?.covered === false ? 'warn' : 'neutral'} heading="The gap — what it leaves">
                {outlook && <>{outlook.lead} </>}
                {gapLine.text}
                {phase2 && <> {gapPhaseTwoLine(phaseNames, phaseTwoGap)}</>}
                {outlook?.verdict && <> {outlook.verdict}</>}
                {outlook?.covered !== false && <> <span className="font-semibold text-slate-900">Step 3 shows the ways to move these numbers.</span></>}
            </PlainWordsBeat>
            {stayingNotAtHome && stayingName && (
                <p className="mt-2 rounded-lg border border-slate-200/70 border-l-4 border-l-amber-500 bg-white px-3 py-2 text-xs leading-relaxed text-amber-900 shadow-sm">
                    <strong>Check one thing:</strong> the profile records {stayingName} as{' '}
                    {RESIDENCY_LABELS[String(stayingResidency)] || 'not living in the family home'}, so the modelling
                    treats the home as having no <JargonTerm term="protected person" /> in it — which raises the
                    aged care means test. If {stayingName} actually lives in the family home, update Living
                    Arrangements in the Client Profile first.
                </p>
            )}
        </PlainWordsCard>
    );
};

export default WalkInPositionNote;
